import { ReportDocument } from "@/components/report-print/ReportDocument";
import type {
  ReportColumn,
  ReportDocumentModel,
  ReportTableRow,
} from "@/components/report-print/reportTypes";
import type {
  MensalExportDocument,
  MensalExportOrder,
  MensalExportProduct,
} from "@/features/supplier/types";
import { compactVolumeDisplay } from "./volumeDisplay";

interface MensalPrintDocumentProps {
  document: MensalExportDocument;
  printMode?: "source" | "preview";
}

function orderColumns(hasVolumeSummary: boolean): ReportColumn[] {
  return [
    { key: "date", label: "Data", width: "17%", noWrap: true },
    { key: "number", label: "Pedido", width: "23%" },
    {
      key: "stores",
      label: "Lojas",
      width: "12%",
      align: "right",
      noWrap: true,
    },
    {
      key: "boxes",
      label: hasVolumeSummary ? "Volumes" : "Caixas",
      width: "12%",
      align: "right",
      noWrap: true,
    },
    {
      key: "gross",
      label: "Bruto",
      width: "18%",
      align: "right",
      noWrap: true,
    },
    {
      key: "commission",
      label: "Comissão",
      width: "18%",
      align: "right",
      noWrap: true,
    },
  ];
}

const EMPTY_ORDER_COLUMNS: ReportColumn[] = [
  { key: "description", label: "Pedidos", width: "100%" },
];

const PRODUCT_COLUMNS: ReportColumn[] = [
  { key: "product", label: "Produto", width: "46%" },
  {
    key: "quantity",
    label: "Qtd.",
    width: "14%",
    align: "right",
    noWrap: true,
  },
  {
    key: "unitPrice",
    label: "Valor",
    width: "18%",
    align: "right",
    noWrap: true,
  },
  {
    key: "subtotal",
    label: "Subtotal",
    width: "22%",
    align: "right",
    noWrap: true,
  },
];

export function buildMensalReportModel(
  document: MensalExportDocument,
): ReportDocumentModel {
  const hasVolumeSummary = Boolean(document.volume_summary);
  const metricRows = [
    [
      { label: "Pedidos", value: String(document.total_orders) },
      {
        label: hasVolumeSummary ? "Volumes" : "Caixas",
        value:
          compactVolumeDisplay(document.volume_summary) ?? document.total_boxes,
      },
      { label: "Bruto", value: document.total_gross_formatted },
    ],
    [
      {
        label: "Comissão",
        value: document.commission_formatted,
        tone: "highlight" as const,
      },
      { label: "Taxa", value: `${document.commission_rate_formatted}%` },
    ],
  ];

  return {
    header: {
      brand: "EloLink",
      logoSvg: document.logo_svg,
      documentLabel: "Comissão Mensal",
      title: document.supplier.name,
      subtitle: document.month,
      dateLabel: `${document.period_start} a ${document.period_end}`,
      dateSubLabel: "",
    },
    metricRows,
    sections: [
      ordersSection(document.orders, hasVolumeSummary),
      productsSection(document.products),
    ],
    footer: {
      brand: document.responsible.name || "EloLink",
      generatedLabel: `Gerado em ${document.generated_at}`,
      documentNumber: document.number,
    },
    continuationContext: `Comissão Mensal · ${document.supplier.name}`,
  };
}

export function MensalPrintDocument({
  document,
  printMode = "source",
}: MensalPrintDocumentProps) {
  return (
    <ReportDocument
      document={buildMensalReportModel(document)}
      printMode={printMode}
    />
  );
}

function ordersSection(orders: MensalExportOrder[], hasVolumeSummary: boolean) {
  return {
    id: "mensal-orders",
    title: "Pedidos do mês",
    subtitle: "Movimento por pedido",
    columns:
      orders.length > 0 ? orderColumns(hasVolumeSummary) : EMPTY_ORDER_COLUMNS,
    rows:
      orders.length > 0
        ? orders.map(buildOrderRow)
        : [
            emptyRow(
              "mensal-orders-empty",
              "Nenhum pedido para este período.",
              EMPTY_ORDER_COLUMNS,
            ),
          ],
  };
}

function productsSection(products: MensalExportProduct[]) {
  return {
    id: "mensal-products",
    title: "Produtos consolidados",
    subtitle: "Totais por produto e embalagem",
    columns: PRODUCT_COLUMNS,
    rows:
      products.length > 0
        ? products.map(buildProductRow)
        : [
            emptyRow(
              "mensal-products-empty",
              "Nenhum produto para este período.",
              PRODUCT_COLUMNS,
            ),
          ],
  };
}

function buildOrderRow(
  order: MensalExportOrder,
  index: number,
): ReportTableRow {
  return {
    id: `mensal-order-${index}`,
    cells: [
      { columnKey: "date", value: order.date },
      { columnKey: "number", value: order.number },
      { columnKey: "stores", value: String(order.stores), align: "right" },
      {
        columnKey: "boxes",
        value: compactVolumeDisplay(order.volume_summary) ?? order.boxes,
        align: "right",
      },
      { columnKey: "gross", value: order.gross_formatted, align: "right" },
      {
        columnKey: "commission",
        value: order.commission_formatted,
        align: "right",
        emphasis: true,
      },
    ],
  };
}

function buildProductRow(
  product: MensalExportProduct,
  index: number,
): ReportTableRow {
  return {
    id: `mensal-product-${index}`,
    density: isLongMensalRowName(product.name) ? "wrapped" : "normal",
    cells: [
      {
        columnKey: "product",
        value: product.name,
        secondary: productSecondary(product.package, product.weight),
      },
      {
        columnKey: "quantity",
        value: product.volume_summary ?? product.total_boxes,
        align: "right",
      },
      {
        columnKey: "unitPrice",
        value: product.unit_price,
        secondary: product.unit_price_label,
        align: "right",
      },
      {
        columnKey: "subtotal",
        value: product.subtotal,
        align: "right",
        emphasis: true,
      },
    ],
  };
}

function emptyRow(
  id: string,
  message: string,
  columns: ReportColumn[],
): ReportTableRow {
  const messageColumn = columns.find((column) => !column.noWrap) ?? columns[0];

  return {
    id,
    tone: "muted",
    cells: messageColumn
      ? [{ columnKey: messageColumn.key, value: message }]
      : [],
  };
}

function productSecondary(
  packageLabel: string | undefined,
  weight: string | undefined,
) {
  return [packageLabel, weight].filter(Boolean).join(" · ");
}

function isLongMensalRowName(value: string) {
  return value.length > 42;
}
